/**
 * ============================================================================
 * Iron Meridian - Map Overlay Controls
 * ============================================================================
 * 
 * Toggleable overlay controls for the world map. Allows players to visualize
 * diplomatic relationships, alliances, and stance distributions.
 * 
 * OVERLAY TYPES:
 * - None: Standard ownership coloring
 * - Alliance: Highlights allied nations with shared color
 * - Stance: Colors nations by diplomatic stance (hostile→ally gradient)
 * - Treaties: Shows treaty connections with line overlays
 */

import { memo } from 'react'

export const OVERLAY_MODES = {
  NONE: 'none',
  ALLIANCE: 'alliance',
  STANCE: 'stance',
  TREATIES: 'treaties'
}

export const OVERLAY_CONFIG = {
  none: {
    id: 'none',
    label: 'Standard',
    icon: '🗺️',
    description: 'Normal ownership view'
  },
  alliance: {
    id: 'alliance',
    label: 'Alliances',
    icon: '🛡️',
    description: 'Highlight allied nations'
  },
  stance: {
    id: 'stance',
    label: 'Stance',
    icon: '🎯',
    description: 'Color by diplomatic stance'
  },
  treaties: {
    id: 'treaties',
    label: 'Treaties',
    icon: '📜',
    description: 'Show treaty connections'
  }
}

// Stance-based color palette for overlay
export const STANCE_COLORS = {
  ALLY: '#22c55e',      // Green
  FRIENDLY: '#86efac',  // Light green
  NEUTRAL: '#9ca3af',   // Gray
  WARY: '#fbbf24',      // Yellow/Amber
  HOSTILE: '#ef4444'    // Red
}

// Alliance overlay colors (for grouping)
export const ALLIANCE_COLORS = {
  player_alliance: '#3b82f6',  // Blue for player's alliance bloc
  enemy_alliance: '#ef4444',   // Red for hostile blocs
  neutral: '#6b7280'           // Gray for neutral
}

function MapOverlayControls({ 
  activeOverlay = 'none', 
  onOverlayChange,
  disabled = false,
  className = ''
}) {
  return (
    <div className={`flex items-center gap-1 ${className}`}>
      <span className="text-xs text-gray-500 mr-1">View:</span>
      {Object.entries(OVERLAY_CONFIG).map(([key, config]) => (
        <button
          key={key}
          onClick={() => onOverlayChange(key)}
          disabled={disabled}
          title={config.description}
          className={`
            px-2 py-1 text-xs rounded transition-all duration-200
            ${activeOverlay === key
              ? 'bg-war-room-accent/20 text-war-room-accent border border-war-room-accent/50'
              : 'bg-war-room-700/50 text-gray-400 border border-transparent hover:bg-war-room-600/50 hover:text-gray-300'
            }
            ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
          `}
        >
          <span className="mr-1">{config.icon}</span>
          {config.label}
        </button>
      ))}
    </div>
  )
}

/**
 * Get the overlay color for a country based on active overlay mode
 * @param {string} countryId - Country ISO-3 code
 * @param {string} overlayMode - Active overlay mode
 * @param {Object} diplomacy - Full diplomacy state
 * @param {string} playerCountry - Player's country ISO-3
 * @returns {string|null} - Hex color or null to use default
 */
export function getOverlayColor(countryId, overlayMode, diplomacy, playerCountry) {
  if (overlayMode === 'none' || !diplomacy) {
    return null // Use default ownership coloring
  }

  // Player's own country
  if (countryId === playerCountry) {
    return '#3b82f6' // Blue for player
  }

  const entry = diplomacy[countryId]
  if (!entry) {
    return '#4b5563' // Gray for unknown
  }

  if (overlayMode === 'stance') {
    return STANCE_COLORS[entry.stance] || STANCE_COLORS.NEUTRAL
  }

  if (overlayMode === 'alliance') {
    // Check if in alliance with player
    const hasAlliance = entry.treaties?.some(t => t.type === 'ALLIANCE')
    if (hasAlliance) {
      return ALLIANCE_COLORS.player_alliance
    }
    if (entry.stance === 'HOSTILE') {
      return ALLIANCE_COLORS.enemy_alliance
    }
    return ALLIANCE_COLORS.neutral
  }

  if (overlayMode === 'treaties') {
    // Color based on whether any treaty exists
    if (entry.treaties?.length > 0) {
      return '#8b5cf6' // Purple for treaty partners
    }
    return '#4b5563' // Gray for no treaties
  }

  return null
}

/**
 * Get treaty connection lines for the treaties overlay
 * Returns array of { from: centroid, to: centroid, type: treatyType }
 */
export function getTreatyConnections(diplomacy, playerCountry, countryCentroids) {
  if (!diplomacy || !countryCentroids) return []

  const connections = []
  const playerCentroid = countryCentroids[playerCountry]
  if (!playerCentroid) return []

  for (const [iso3, entry] of Object.entries(diplomacy)) {
    if (!entry.treaties || entry.treaties.length === 0) continue
    const targetCentroid = countryCentroids[iso3]
    if (!targetCentroid) continue

    for (const treaty of entry.treaties) {
      connections.push({
        from: playerCentroid,
        to: targetCentroid,
        type: treaty.type,
        countryId: iso3,
        treatyId: treaty.id
      })
    }
  }

  return connections
}

export default memo(MapOverlayControls)
